import { LAppDefine, NpcModel } from "./src/LAppDefine";
import { LAppLive2DManager } from "./src/LAppLive2DManager";
import { Live2D } from "./src/live2d";
import { L2DMatrix44, L2DTargetPoint, L2DViewMatrix } from "./src/Live2DFramework";
import { MatrixStack } from "./src/MatrixStack";

export class Live2dDelegateV2 {
  private static instance: Live2dDelegateV2;
  live2DMgr: LAppLive2DManager;
  isDrawStart = false;
  gl = null; // ドラッグによるアニメーションの管理
  dragMgr = null; /*new L2DTargetPoint();*/
  viewMatrix = null; /*new L2DViewMatrix();*/
  projMatrix = null; /*new L2DMatrix44()*/
  deviceToScreen = null; /*new L2DMatrix44();*/
  drag = false; // ドラッグ中かどうか
  oldLen = 0; // 二本指タップした時の二点間の距離
  lastMouseX = 0;
  lastMouseY = 0;
  isModelShown = false;

  constructor(private resourceRootPath: string, private canvas: HTMLCanvasElement) {
    this.gl = this.getWebGLContext();
    if (!this.gl) {
      this.l2dError("Failed to create WebGL context.");
      return;
    }
    this.initEventListener();
    this.init();
  }

  static get Instance(): Live2dDelegateV2 {
    if (!this.instance) {
      console.warn("Live2dDelegateV2.init first please !");
      return null;
    }
    return this.instance;
  }

  static init(resourceRootPath: string, canvas: HTMLCanvasElement) {
    this.instance = new Live2dDelegateV2(resourceRootPath, canvas);
  }

  showTalk() {
    this.live2DMgr.talkEvent();
  }

  initEventListener() {
    window.addEventListener("mousemove", (e) => this.followPointer(e));
  }

  init() {
    // 3Dバッファの初期化
    const width = this.canvas.width;
    const height = this.canvas.height;

    this.dragMgr = new L2DTargetPoint();

    // ビュー行列
    const ratio = height / width;
    const left = LAppDefine.VIEW_LOGICAL_LEFT;
    const right = LAppDefine.VIEW_LOGICAL_RIGHT;
    const bottom = -ratio;
    const top = ratio;

    this.viewMatrix = new L2DViewMatrix();

    // デバイスに対応する画面の範囲。 Xの左端, Xの右端, Yの下端, Yの上端
    this.viewMatrix.setScreenRect(left, right, bottom, top);

    // デバイスに対応する画面の範囲。 Xの左端, Xの右端, Yの下端, Yの上端
    this.viewMatrix.setMaxScreenRect(
      LAppDefine.VIEW_LOGICAL_MAX_LEFT,
      LAppDefine.VIEW_LOGICAL_MAX_RIGHT,
      LAppDefine.VIEW_LOGICAL_MAX_BOTTOM,
      LAppDefine.VIEW_LOGICAL_MAX_TOP
    );

    this.viewMatrix.setMaxScale(LAppDefine.VIEW_MAX_SCALE);
    this.viewMatrix.setMinScale(LAppDefine.VIEW_MIN_SCALE);

    this.projMatrix = new L2DMatrix44();
    this.projMatrix.multScale(1, width / height);

    // マウス用スクリーン変換行列
    this.deviceToScreen = new L2DMatrix44();
    this.deviceToScreen.multTranslate(-width / 2.0, -height / 2.0);
    this.deviceToScreen.multScale(2 / width, -2 / width);

    // OpenGLのコンテキストをセット
    Live2D.setGL(this.gl);

    this.live2DMgr = new LAppLive2DManager(this.resourceRootPath, this.gl);

    // 描画エリアを白でクリア
    this.gl.clearColor(0.0, 0.0, 0.0, 0.0);

    this.startDraw();
  }

  startDraw() {
    if (!this.isDrawStart) {
      this.isDrawStart = true;
      const tick = () => {
        this.draw();
        requestAnimationFrame(tick);
      };
      tick();
    }
  }

  draw() {
    // l2dLog("--> draw()");

    MatrixStack.reset();
    MatrixStack.loadIdentity();

    this.dragMgr.update(); // ドラッグ用パラメータの更新
    this.live2DMgr.setDrag(this.dragMgr.getX(), this.dragMgr.getY());

    // Canvasをクリアする
    this.gl.clear(this.gl.COLOR_BUFFER_BIT);

    MatrixStack.multMatrix(this.projMatrix.getArray());
    MatrixStack.multMatrix(this.viewMatrix.getArray());
    MatrixStack.push();

    for (let i = 0; i < this.live2DMgr.numModels(); i++) {
      const model = this.live2DMgr.getModel(i);

      if (model == null) return;

      if (model.initialized && !model.updating) {
        model.update();
        model.draw(this.gl);

        if (!this.isModelShown && i == this.live2DMgr.numModels() - 1) {
          this.isModelShown = !this.isModelShown;
        }
      }
    }

    MatrixStack.pop();
  }

  changeModel(model: NpcModel) {
    this.isModelShown = false;
    this.live2DMgr.reloadFlg = true;
    this.live2DMgr.changeModel(model);
  }

  /* ********** マウスイベント ********** */

  /*
   * マウスホイールによる拡大縮小
   */
  modelScaling(scale) {
    const isMaxScale = this.viewMatrix.isMaxScale();
    const isMinScale = this.viewMatrix.isMinScale();

    this.viewMatrix.adjustScale(0, 0, scale);

    // 画面が最大になったときのイベント
    if (!isMaxScale) {
      if (this.viewMatrix.isMaxScale()) {
        this.live2DMgr.maxScaleEvent();
      }
    }
    // 画面が最小になったときのイベント
    if (!isMinScale) {
      if (this.viewMatrix.isMinScale()) {
        this.live2DMgr.minScaleEvent();
      }
    }
  }

  /*
   * クリックされた方向を向く
   * タップされた場所に応じてモーションを再生
   */
  modelTurnHead(event) {
    this.drag = true;

    const rect = event.target.getBoundingClientRect();

    const sx = this.transformScreenX(event.clientX - rect.left);
    const sy = this.transformScreenY(event.clientY - rect.top);
    const vx = this.transformViewX(event.clientX - rect.left);
    const vy = this.transformViewY(event.clientY - rect.top);

    if (LAppDefine.DEBUG_MOUSE_LOG)
      this.l2dLog(
        "onMouseDown device( x:" + event.clientX + " y:" + event.clientY + " ) view( x:" + vx + " y:" + vy + ")"
      );

    this.lastMouseX = sx;
    this.lastMouseY = sy;

    this.dragMgr.setPoint(vx, vy); // その方向を向く

    // タップした場所に応じてモーションを再生
    this.live2DMgr.tapEvent(vx, vy);
  }

  /*
   * マウスを動かした時のイベント
   */
  followPointer(event: MouseEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const vx = this.transformViewX(event.clientX - rect.left);
    const vy = this.transformViewY(event.clientY - rect.top);
    this.dragMgr.setPoint(vx, vy);
  }

  /*
   * 正面を向く
   */
  lookFront() {
    if (this.drag) {
      this.drag = false;
    }

    this.dragMgr.setPoint(0, 0);
  }

  /* ********** マトリックス操作 ********** */

  transformViewX(deviceX) {
    const screenX = this.deviceToScreen.transformX(deviceX); // 論理座標変換した座標を取得。
    return this.viewMatrix.invertTransformX(screenX); // 拡大、縮小、移動後の値。
  }

  transformViewY(deviceY) {
    const screenY = this.deviceToScreen.transformY(deviceY); // 論理座標変換した座標を取得。
    return this.viewMatrix.invertTransformY(screenY); // 拡大、縮小、移動後の値。
  }

  transformScreenX(deviceX) {
    return this.deviceToScreen.transformX(deviceX);
  }

  transformScreenY(deviceY) {
    return this.deviceToScreen.transformY(deviceY);
  }

  /*
   * WebGLのコンテキストを取得する
   */
  getWebGLContext() {
    const NAMES = ["webgl", "experimental-webgl", "webkit-3d", "moz-webgl"];

    for (let i = 0; i < NAMES.length; i++) {
      try {
        const ctx = this.canvas.getContext(NAMES[i], {
          premultipliedAlpha: true,
        });
        if (ctx) return ctx;
      } catch (e) {}
    }
    return null;
  }

  /*
   * 画面ログを出力
   */
  l2dLog(msg) {
    if (!LAppDefine.DEBUG_LOG) return;

    const myconsole = document.getElementById("myconsole");
    myconsole.innerHTML = myconsole.innerHTML + "<br>" + msg;

    console.log(msg);
  }

  /*
   * 画面エラーを出力
   */
  l2dError(msg) {
    if (!LAppDefine.DEBUG_LOG) return;

    this.l2dLog("<span style='color:red'>" + msg + "</span>");

    console.error(msg);
  }
}
